import { compact } from "es-toolkit";
import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import { documentNumber } from "./document-number";
import {
	printedPrice,
	receiptAmountLine,
	receiptTableWidths,
	receiptText,
	receiptTotalLine,
	saleDiscountCents,
	sellerLines,
	vatTable,
} from "./receipt";
import { lineName, vatBreakdown } from "./sale-lines";

/** How much of the sale's fingerprint the receipt prints as its security mark. */
export const FINGERPRINT_EXTRACT_LENGTH = 12;

interface ReceiptData {
	sale: Sale;
	saleProducts: SaleProduct[];
	salePayments: SalePayment[];
	store: Store;
	t: TFunction<"translation", undefined>;
	columns: number;
}

/**
 * The ticket de caisse. Printed on demand only, never automatically: a
 * customer who wants one asks for it.
 *
 * The store has no snapshot on the sale, so the seller is printed as the store
 * is today.
 */
export const formatDataPrinterReceipt = ({
	sale,
	saleProducts,
	salePayments,
	store,
	t,
	columns,
}: ReceiptData): ReceiptLine[] => {
	const width = receiptTableWidths(columns);
	const price = (cents: number) =>
		printedPrice(cents, sale.totalAmountCurrency);
	const discountCents = saleDiscountCents(sale, saleProducts);

	const productRow = (
		quantity: string,
		name: string,
		amount: string,
	): ReceiptLine => ({
		kind: "columns",
		cols: [
			{ text: quantity, width: width.quantity },
			{ text: name, width: width.name },
			{ text: amount, width: width.price, align: "right" },
		],
	});

	return [
		...sellerLines(
			{
				name: store.name,
				legalName: store.legalName,
				addressLines: compact([
					store.address1,
					store.address2,
					compact([store.zipcode, store.city]).join(" "),
				]),
				// The store's contact details are optional on the record, so skip the
				// blank lines rather than feeding paper for them.
				contactLines: compact([
					store.phoneNumber,
					store.emailAddress,
					store.websiteUrl,
				]),
				siret: store.siret,
				vatNumber: store.vatNumber,
			},
			t,
			columns,
		),
		{ kind: "feed", lines: 1 },
		{
			kind: "text",
			text: DateTime.fromISO(sale.createdAt).toFormat("dd/MM/yyyy HH:mm"),
			align: "center",
		},
		{
			kind: "text",
			text: t("print.receipt.receiptNumber", {
				receiptNumber: documentNumber(sale.number),
			}),
			align: "center",
			bold: true,
		},
		...(sale.imported
			? receiptText(t("print.receipt.imported"), columns, {
					align: "center",
				})
			: []),
		{ kind: "feed", lines: 1 },

		productRow(
			t("print.receipt.quantity"),
			t("print.receipt.productName"),
			t("print.receipt.price"),
		),
		{ kind: "rule" },
		...saleProducts.map((saleProduct) =>
			productRow(
				saleProduct.quantity.toString(),
				lineName(saleProduct),
				price(saleProduct.amountCents),
			),
		),
		{ kind: "rule" },

		...(discountCents > 0
			? [
					receiptTotalLine(
						t("print.receipt.discount", { amount: price(-discountCents) }),
						columns,
					),
				]
			: []),
		receiptTotalLine(
			t("print.receipt.totalAmount", { amount: price(sale.totalAmountCents) }),
			columns,
		),
		{ kind: "feed", lines: 1 },

		...vatTable(
			vatBreakdown(saleProducts),
			sale.totalAmountCurrency,
			t,
			columns,
		),
		{ kind: "feed", lines: 1 },

		...salePayments.map((payment) =>
			receiptAmountLine(
				payment.paymentType?.name ?? "",
				printedPrice(payment.totalAmountCents, payment.totalAmountCurrency),
				columns,
			),
		),
		{ kind: "feed", lines: 1 },

		{
			kind: "text",
			text: t("print.receipt.fingerprint", {
				fingerprint: sale.fingerprint?.slice(0, FINGERPRINT_EXTRACT_LENGTH),
			}),
			align: "center",
		},
		{ kind: "feed", lines: 3 },
		{ kind: "cut" },
	];
};
